import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Trophy, Users, Target, Flag, CirclePlay, BarChart3 } from 'lucide-react'
import Footer from '../components/Footer'
import { useTranslation } from '../hooks/use-i18n'

const mockPlayers = [
  { id: 'p1', name: 'Alex Rivera', handicap: 12, level: 3, sessions: 14 },
  { id: 'p2', name: 'Maya Chen', handicap: 15, level: 2, sessions: 11 },
  { id: 'p3', name: 'Jordan Lee', handicap: 18, level: 2, sessions: 9 },
  { id: 'p4', name: 'Samir Patel', handicap: 20, level: 1, sessions: 6 },
]

const mockRanking = [
  { rank: 1, name: 'Alex Rivera', points: 2840, accuracy: '73%' },
  { rank: 2, name: 'Maya Chen', points: 2690, accuracy: '69%' },
  { rank: 3, name: 'Jordan Lee', points: 2510, accuracy: '66%' },
  { rank: 4, name: 'Samir Patel', points: 2290, accuracy: '62%' },
  { rank: 5, name: 'Lina Ortiz', points: 2140, accuracy: '58%' },
]

const steps = [
  {
    icon: CirclePlay,
    titleKey: 'golf.steps.title1',
    bodyKey: 'golf.steps.body1',
  },
  {
    icon: Target,
    titleKey: 'golf.steps.title2',
    bodyKey: 'golf.steps.body2',
  },
  {
    icon: Trophy,
    titleKey: 'golf.steps.title3',
    bodyKey: 'golf.steps.body3',
  },
]

const Golf: React.FC = () => {
  const { t } = useTranslation()

  return (
    <div className="min-h-screen bg-background">
      <section className="pt-16 sm:pt-20 pb-10 sm:pb-12 gradient-bg-subtle relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="text-center mb-8 sm:mb-10"
          >
            <span className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-sm font-semibold">
              <Flag className="w-4 h-4" />
              {t('golf.badge')}
            </span>
            <h1 className="mt-4 font-display text-display-1 font-bold text-foreground">
              {t('golf.title')}
            </h1>
            <p className="mt-3 text-body-lg text-muted-foreground max-w-2xl mx-auto">
              {t('golf.subtitle')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.45 }}
            className="glass-card rounded-3xl p-3 sm:p-4 border border-border/60"
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/golf-hero.png`}
              alt={t('golf.imageAlt')}
              className="w-full rounded-2xl object-cover"
            />
          </motion.div>
        </div>
      </section>

      <section className="py-10 sm:py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            {steps.map((step, i) => (
              <motion.div
                key={step.titleKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="glass-card rounded-2xl p-5 border border-border/50"
              >
                <step.icon className="w-7 h-7 text-emerald-400 mb-3" />
                <h3 className="text-lg font-semibold text-foreground mb-2">{t(step.titleKey)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t(step.bodyKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-8 sm:py-10">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="xl:col-span-2 glass-card rounded-2xl border border-border/50 overflow-hidden"
            >
              <div className="p-4 sm:p-5 border-b border-border/60 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h2 className="font-display text-xl font-bold text-foreground">{t('golf.leaderboardTitle')}</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground border-b border-border/40">
                      <th className="text-left p-3 sm:p-4 font-medium">{t('golf.rank')}</th>
                      <th className="text-left p-3 sm:p-4 font-medium">{t('golf.player')}</th>
                      <th className="text-left p-3 sm:p-4 font-medium">{t('golf.points')}</th>
                      <th className="text-left p-3 sm:p-4 font-medium">{t('golf.accuracy')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mockRanking.map((row) => (
                      <tr key={row.rank} className="border-b border-border/30 last:border-none">
                        <td className="p-3 sm:p-4 font-semibold text-foreground">#{row.rank}</td>
                        <td className="p-3 sm:p-4 text-foreground">{row.name}</td>
                        <td className="p-3 sm:p-4 text-foreground">{row.points}</td>
                        <td className="p-3 sm:p-4 text-muted-foreground">{row.accuracy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 }}
              className="glass-card rounded-2xl border border-border/50 p-4 sm:p-5"
            >
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-primary" />
                <h2 className="font-display text-xl font-bold text-foreground">{t('golf.playersTitle')}</h2>
              </div>
              <div className="space-y-3">
                {mockPlayers.map((player) => (
                  <div key={player.id} className="rounded-xl bg-muted/30 border border-border/40 p-3">
                    <p className="font-semibold text-foreground">{player.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('golf.playerMeta', {
                        handicap: String(player.handicap),
                        level: String(player.level),
                        sessions: String(player.sessions),
                      })}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-14">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-strong rounded-3xl p-6 sm:p-10 text-center max-w-3xl mx-auto border border-primary/20"
          >
            <h2 className="font-display text-display-2 font-bold text-foreground mb-3">{t('golf.ctaTitle')}</h2>
            <p className="text-muted-foreground mb-6">
              {t('golf.ctaBody')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Link to="/showcase" className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity">
                {t('golf.ctaPrimary')}
              </Link>
              <Link to="/login" className="px-6 py-3 rounded-xl border border-border bg-muted/30 text-foreground font-semibold hover:bg-muted/50 transition-colors">
                {t('golf.ctaSecondary')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Golf
